import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { isCatalogSettings } from "../elements/catalog";
import { roomRemapEnabledEntityTypes } from "../remap/remapDocEntityTypes";
import { entityIsTargetedSettings, entityIsTargetedSettingsSet } from "../settingsTools";
import type {
  DocClipboardReadableEntityHandle,
  DocClipboardReadableEntityNode,
  DocClipboardWritableEntityHandle,
  DocClipboardWritableEntityNode,
  DocClipboardWritableEntityType,
} from "./docClipboardEntityTypes";
import {
  docClipboardReadableEntities,
  docClipboardWritableEntities,
  docClipboardWritableEntityTypes,
} from "./docClipboardEntityTypes";
import { docEntityGroupNodeIs } from "./docEntityGroupNodeIs";
import type { DocEntityNode } from "./docEntityNode";
import type { DocEntityNodeSource } from "./docEntityNodeRegistryTypes";

function docEntityIsClipboardEligible(doc: Doc, handle: DocEntityHandle, source: DocEntityNodeSource): boolean {
  switch (handle.type) {
    case "SETTINGS_SET": {
      // SETTINGS_SET is only checked in writable function
      const node = source.getNode(doc, handle);
      assertIsNotUndefined(node);

      const settingsSet = node.getDocEntity();
      assertIsNotUndefined(settingsSet);

      return !entityIsTargetedSettingsSet(settingsSet);
    }
    case "SETTINGS": {
      const node = source.getNode(doc, handle);
      assertIsNotUndefined(node);

      const settings = node.getDocEntity();
      assertIsNotUndefined(settings);

      const isCatalogSetting = isCatalogSettings(settings);

      const isTargetedSetting = entityIsTargetedSettings(doc, settings);
      return !isCatalogSetting && !isTargetedSetting;
    }
    case "GROUP": {
      const node = source.getNode(doc, handle);
      assertIsNotUndefined(node);

      const isBuiltInGroup = docEntityGroupNodeIs(node, "isBuiltIn");
      const isContentMutableBuiltInGroup = docEntityGroupNodeIs(node, "isContentMutableBuiltIn");

      const childrenGroupable = node.getChildren().every((child) => child.getConfig()?.isClipboardWritable);
      return !isBuiltInGroup && !isContentMutableBuiltInGroup && childrenGroupable;
    }
    case "CATALOG_ITEM": {
      return false;
    }
    default: {
      return true;
    }
  }
}

export function docEntityIsClipboardWritable(
  doc: Doc,
  handle: DocEntityHandle,
  registry: DocEntityNodeSource,
): handle is DocClipboardWritableEntityHandle {
  const clipboardWritable = docClipboardWritableEntities[handle.type];

  if (!clipboardWritable) {
    return false;
  }

  return docEntityIsClipboardEligible(doc, handle, registry);
}

export function docEntityIsClipboardReadable(
  doc: Doc,
  handle: DocEntityHandle,
  registry: DocEntityNodeSource,
): handle is DocClipboardReadableEntityHandle {
  const clipboardReadable = docClipboardReadableEntities[handle.type];

  if (!clipboardReadable) {
    return false;
  }

  return docEntityIsClipboardEligible(doc, handle, registry);
}

export function isDocClipboardWritableEntityType(
  entityType: DocEntityType,
): entityType is DocClipboardWritableEntityType {
  return docClipboardWritableEntityTypes.includes(entityType as DocClipboardWritableEntityType);
}

export function isDocClipboardWritableEntityHandle(
  handle: DocEntityHandle,
): handle is DocClipboardWritableEntityHandle {
  return !!docClipboardWritableEntities[handle?.type];
}

export function isDocClipboardWritableEntityNode(node: DocEntityNode): node is DocClipboardWritableEntityNode {
  const config = node.getConfig();
  return !!config?.isClipboardWritable;
}

export function isDocClipboardReadableEntityHandle(
  handle: DocEntityHandle,
): handle is DocClipboardReadableEntityHandle {
  return !!docClipboardReadableEntities[handle?.type];
}

export function isDocClipboardReadableEntityNode(node: DocEntityNode): node is DocClipboardReadableEntityNode {
  const config = node.getConfig();
  return !!config?.isClipboardReadable;
}

export const clipboardRemapEnabledEntityTypes = roomRemapEnabledEntityTypes.filter((entityType) =>
  isDocClipboardWritableEntityType(entityType),
);
